import { Table } from "@tiptap/extension-table";

/**
 * Wraps the rendered `<table>` in a `.tableWrapper` div so a wide table can
 * scroll horizontally instead of overflowing the post/editor column.
 *
 * Tiptap's own `Table` node view does this too (see `TableView` in
 * `@tiptap/extension-table`), but that view only gets registered when the
 * `columnResizing` ProseMirror plugin is active - which requires
 * `resizable: true` *and* `editor.isEditable` (see `Table`'s
 * `addProseMirrorPlugins`). Circle configures `resizable: false` for all
 * tables, and even with it enabled the wrapper still wouldn't apply to
 * read-only rendering (post feeds, etc.), since `isResizable` also checks
 * editability. Without that NodeView, Tiptap falls back to this node's
 * plain `renderHTML`/`toDOM` output, which has no wrapper at all.
 *
 * A wrapper div (rather than `overflow-x`/`max-width` on the `<table>`
 * element itself) is needed for two reasons:
 * - `display: table` boxes don't reliably establish a scroll container, so
 *   `overflow-x: auto` on the table itself can be a no-op.
 * - Tiptap's own `renderHTML` sets an inline `min-width` on the table (see
 *   `createColGroup`, computed from any stored per-column widths - e.g. a
 *   table pasted in from a spreadsheet/Notion/Docs). Per the CSS
 *   min/max-width conflict-resolution rules, that inline `min-width` wins
 *   over any `max-width` we set in our own stylesheet, so capping the
 *   table's own width can't prevent it from overflowing on narrow screens.
 *   Scoping the scroll behavior to the wrapper sidesteps that fight
 *   entirely - the table stays whatever width it wants, and the wrapper
 *   just clips/scrolls it.
 */
export const CustomizedTable = Table.extend({
  renderHTML(props) {
    const table = this.parent?.(props);
    if (!table) return ["table", props.HTMLAttributes, ["tbody", 0]];

    return ["div", { class: "tableWrapper" }, table] as const;
  },
});
